import { noop, Observable } from 'rxjs';
import { replaceAllChildren } from '../../helpers/dom/node';
import { htmlToText, nodeToText } from '../../helpers/htmlToText';
import { textToDocumentFragment, textToHtml } from '../../helpers/textToHtml';
import { createConnectingService } from '../base/connectingService';
import { BaseService } from '../base/service';
import { HtmlService } from '../html/htmlService';
import { TextService } from '../text/textService';
import { ElementSelectionService } from './elementSelectionService';
import { ElementService } from './elementService';

export type ElementTextServiceOptions = {
  /** Perform text decoration. */
  decorateText?: (element: Element) => void;
};

export function createElementTextService(
  element$: Observable<Element | null>,
  htmlService: HtmlService,
  selectionService: ElementSelectionService,
  options: ElementTextServiceOptions,
): TextService {
  return createConnectingService<Element, TextService>(
    element$,
    () => new HtmlTextService(htmlService),
    element => new ElementTextService(element, htmlService, selectionService, options),
  );
}

class ElementTextService extends ElementService<Element> implements TextService {
  constructor(
    element: Element,
    htmlService: HtmlService,
    selectionService: ElementSelectionService,
    options: ElementTextServiceOptions = {},
  ) {
    super(element, htmlService);
    this.onDecorateText = () => {
      if (!options.decorateText) return;

      options.decorateText(element);
    };
    this.selectionService = selectionService;
  }

  private readonly selectionService: ElementSelectionService;
  private readonly onDecorateText: () => void;

  get text() {
    return nodeToText(this.element);
  }

  setText(text: string) {
    const fragment = textToDocumentFragment(this.document, text);
    replaceAllChildren(this.element, fragment);
  }

  decorateText() {
    this.selectionService.withMutations(() => {
      this.onDecorateText();
    });
  }
}

class HtmlTextService extends BaseService implements TextService {
  constructor(htmlService: HtmlService) {
    super(htmlService);

    this.htmlService = htmlService;
  }

  private readonly htmlService: HtmlService;

  get text() {
    return htmlToText(this.htmlService.html);
  }

  setText(text: string) {
    const html = textToHtml(text);
    this.htmlService.setCleanHtml(html);
  }

  decorateText = noop;
}
